import * as React from "react";
import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/** tailwind-merge must know the custom type scale, or `text-30` and `text-ok` look like two colours. */
const twMerge = extendTailwindMerge({
  extend: {
    theme: {
      text: ["12", "13", "14", "15", "16", "17", "18", "19", "20", "22", "24", "28", "30", "40", "88"],
      radius: ["card", "panel", "chip", "btn", "tile", "pill"],
    },
  },
});

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

// ---------------------------------------------------------------------------
// Button — one accent action per screen; everything else navy outline or text link.

type ButtonVariant = "accent" | "primary" | "outline" | "ghost" | "danger" | "link";
type ButtonSize = "sm" | "md" | "dock" | "dock-primary" | "icon" | "dock-icon";

const buttonVariants: Record<ButtonVariant, string> = {
  accent: "bg-accent text-white hover:brightness-95 border border-accent",
  primary: "bg-primary text-on-primary border border-primary hover:brightness-110",
  outline: "bg-surface text-primary border border-primary hover:bg-track",
  ghost: "bg-transparent text-primary border border-transparent hover:bg-track",
  danger: "bg-surface text-danger-text border border-danger hover:bg-danger-tint",
  link: "bg-transparent text-primary underline underline-offset-4 border-0 px-0 h-auto hover:text-navy-2",
};
const buttonSizes: Record<ButtonSize, string> = {
  sm: "h-9 px-3 text-14 rounded-btn",
  md: "h-10 px-4 text-14 rounded-btn",
  dock: "h-14 px-5 text-17 rounded-chip border-[length:var(--control-border-width,1px)]",
  "dock-primary": "h-16 px-6 text-19 font-extrabold rounded-card w-full",
  icon: "h-10 w-10 rounded-btn p-0",
  "dock-icon": "h-14 w-14 rounded-chip p-0 border-[length:var(--control-border-width,1px)]",
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "outline", size = "md", className, type = "button", ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        "inline-flex select-none items-center justify-center gap-2 font-semibold transition-[filter,background-color] disabled:cursor-not-allowed disabled:opacity-50",
        buttonSizes[size],
        buttonVariants[variant],
        variant === "accent" && size !== "dock-primary" && "font-bold",
        className,
      )}
      {...props}
    />
  );
});

/** Link styled like a button (for navigation actions). */
export function ButtonLink({
  variant = "outline",
  size = "md",
  className,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & { variant?: ButtonVariant; size?: ButtonSize }) {
  return (
    // eslint-disable-next-line jsx-a11y/anchor-has-content -- children are passed through props
    <a
      className={cn(
        "inline-flex items-center justify-center gap-2 font-semibold no-underline",
        buttonSizes[size],
        buttonVariants[variant],
        className,
      )}
      {...props}
    />
  );
}

// ---------------------------------------------------------------------------
// Form controls

const controlBase =
  "w-full min-w-0 bg-surface text-ink border border-primary/60 placeholder:text-muted/80 disabled:opacity-60 aria-[invalid=true]:border-danger";

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement> & { dock?: boolean }>(
  function Input({ className, dock, ...props }, ref) {
    return (
      <input
        ref={ref}
        className={cn(
          controlBase,
          dock
            ? "h-14 rounded-chip border-[length:var(--control-border-width,2px)] border-primary px-4 text-20 font-bold tracking-[0.04em]"
            : "h-10 rounded-btn px-3 text-15",
          className,
        )}
        {...props}
      />
    );
  },
);

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...props }, ref) {
    return <textarea ref={ref} className={cn(controlBase, "min-h-24 rounded-btn px-3 py-2 text-15", className)} {...props} />;
  },
);

export const Select = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement> & { dock?: boolean }>(
  function Select({ className, dock, ...props }, ref) {
    return (
      <select
        ref={ref}
        className={cn(
          controlBase,
          dock ? "h-14 rounded-chip border-[length:var(--control-border-width,2px)] border-primary px-3 text-17" : "h-10 rounded-btn px-2 text-15",
          className,
        )}
        {...props}
      />
    );
  },
);

export function Label({ className, ...props }: React.LabelHTMLAttributes<HTMLLabelElement>) {
  // eslint-disable-next-line jsx-a11y/label-has-associated-control -- htmlFor is passed by callers
  return <label className={cn("text-13 font-semibold text-muted", className)} {...props} />;
}

/** Label + control + hint + error, wired with ids and aria-describedby. */
export function Field({
  label,
  hint,
  error,
  children,
  className,
  optionalLabel,
}: {
  label: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  children: (props: { id: string; "aria-describedby"?: string; "aria-invalid"?: boolean }) => React.ReactNode;
  className?: string;
  optionalLabel?: string;
}) {
  const id = React.useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;
  return (
    <div className={cn("flex min-w-0 flex-col gap-1", className)}>
      <Label htmlFor={id}>
        {label}
        {optionalLabel ? <span className="font-normal"> ({optionalLabel})</span> : null}
      </Label>
      {children({ id, "aria-describedby": describedBy, "aria-invalid": error ? true : undefined })}
      {hint ? (
        <span id={hintId} className="text-13 text-muted">
          {hint}
        </span>
      ) : null}
      {error ? (
        <span id={errorId} role="alert" className="text-13 font-semibold text-danger-text">
          {error}
        </span>
      ) : null}
    </div>
  );
}

export function Checkbox({
  label,
  className,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { label: React.ReactNode }) {
  const id = React.useId();
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <input id={id} type="checkbox" className="h-5 w-5 accent-[var(--color-primary)]" {...props} />
      <label htmlFor={id} className="text-15">
        {label}
      </label>
    </div>
  );
}

export function Switch({
  checked,
  onCheckedChange,
  label,
  className,
  dock,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  label: string;
  className?: string;
  dock?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        "relative inline-flex shrink-0 items-center rounded-pill border-2 border-primary transition-colors",
        dock ? "h-9 w-16" : "h-6 w-11",
        checked ? "bg-primary" : "bg-surface",
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "inline-block rounded-pill transition-transform",
          dock ? "h-6 w-6" : "h-4 w-4",
          checked ? "bg-on-primary" : "bg-primary",
          checked ? (dock ? "translate-x-8" : "translate-x-5") : "translate-x-1",
        )}
      />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Layout

export function Card({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("rounded-card border border-border bg-surface p-5", className)} {...props} />;
}

export function SectionTitle({ className, children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h2 className={cn("m-0 text-20 font-extrabold", className)} {...props}>
      {children}
    </h2>
  );
}

export function PageHeader({
  label,
  title,
  actions,
}: {
  label?: React.ReactNode;
  title: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex min-w-0 flex-col gap-0.5">
        {label ? <span className="text-13 font-semibold text-muted">{label}</span> : null}
        <h1 className="m-0 text-28 font-extrabold">{title}</h1>
      </div>
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}

export function FactsGrid({ facts, className }: { facts: Array<{ label: React.ReactNode; value: React.ReactNode }>; className?: string }) {
  return (
    <dl className={cn("m-0 grid grid-cols-2 gap-x-4 gap-y-2.5 text-14", className)}>
      {facts.map((f, i) => (
        <div key={i} className="flex min-w-0 flex-col gap-0.5">
          <dt className="text-13 text-muted">{f.label}</dt>
          <dd className="m-0 font-semibold">{f.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function EmptyState({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn("rounded-panel border border-dashed border-border p-6 text-center text-15 text-muted", className)}>{children}</div>;
}

export function Spinner({ label }: { label: string }) {
  return (
    <div role="status" className="flex items-center gap-2 text-15 text-muted">
      <span aria-hidden="true" className="h-4 w-4 animate-spin rounded-pill border-2 border-primary border-t-transparent" />
      {label}
    </div>
  );
}

export function ErrorText({ children }: { children: React.ReactNode }) {
  return (
    <div role="alert" className="rounded-panel border border-danger bg-danger-tint px-3 py-2 text-14 font-semibold text-danger-text">
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tables (admin: dense, scannable)

export function Table({ className, ...props }: React.TableHTMLAttributes<HTMLTableElement>) {
  return (
    <div className="w-full overflow-x-auto rounded-panel border border-border bg-surface">
      <table className={cn("w-full border-collapse text-14", className)} {...props} />
    </div>
  );
}
export function THead(props: React.HTMLAttributes<HTMLTableSectionElement>) {
  return <thead className="bg-ground text-left text-13 text-muted" {...props} />;
}
export function TH({ className, ...props }: React.ThHTMLAttributes<HTMLTableCellElement>) {
  return <th scope="col" className={cn("whitespace-nowrap border-b border-border px-3 py-2 font-semibold", className)} {...props} />;
}
export function TR({ className, ...props }: React.HTMLAttributes<HTMLTableRowElement>) {
  return <tr className={cn("border-b border-border-soft last:border-b-0", className)} {...props} />;
}
export function TD({ className, ...props }: React.TdHTMLAttributes<HTMLTableCellElement>) {
  return <td className={cn("px-3 py-2 align-middle", className)} {...props} />;
}
